import { useUser } from "@clerk/clerk-react";
import { useAction, useQuery } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Footer } from "../components/footer";
import { Navbar } from "../components/navbar";
import { formatCurrencyFromCents } from "@/lib/utils";

export default function DashboardPaid() {
  const { user } = useUser();
  const userData = useQuery(
    api.users.getUserByToken,
    user?.id ? { tokenIdentifier: user.id } : "skip"
  );
  const subscription = useQuery(api.subscriptions.getUserSubscription);
  const getDashboardUrl = useAction(api.subscriptionsActions.getUserDashboardUrl);

  const handleManageSubscription = async () => {
    try {
      const result = await getDashboardUrl({
        customerId: subscription?.customerId,
      });
      if (result?.url) {
        window.location.href = result.url;
      }
    } catch (error) {
      console.error("Error getting dashboard URL:", error);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-gray-50">
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="mt-2 text-gray-600">View and manage your account information</p>
            <div className="mt-[1rem]">
              <button
                onClick={handleManageSubscription}
                className="rounded-lg bg-blue-600 px-4 py-2 text-white transition-colors hover:bg-blue-700"
              >
                Manage Subscription
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Clerk User Data */}
            <DataCard title="Clerk User Information">
              <div className="space-y-2">
                <DataRow label="Full Name" value={user?.fullName} />
                <DataRow label="Email" value={user?.primaryEmailAddress?.emailAddress} />
                <DataRow label="User ID" value={user?.id} />
                <DataRow
                  label="Created"
                  value={new Date(user?.createdAt || "").toLocaleDateString()}
                />
                <DataRow
                  label="Email Verified"
                  value={
                    user?.primaryEmailAddress?.verification.status === "verified" ? "Yes" : "No"
                  }
                />
              </div>
            </DataCard>

            {/* Database User Data */}
            <DataCard title="Database User Information">
              <div className="space-y-2">
                <DataRow label="Database ID" value={userData?._id} />
                <DataRow label="Name" value={userData?.name} />
                <DataRow label="Email" value={userData?.email} />
                <DataRow label="Token ID" value={userData?.tokenIdentifier} />
                <DataRow
                  label="Last Updated"
                  value={
                    userData?._creationTime
                      ? new Date(userData._creationTime).toLocaleDateString()
                      : undefined
                  }
                />
              </div>
            </DataCard>

            {/* Session Information */}
            <DataCard title="Current Session">
              <div className="space-y-2">
                <DataRow
                  label="Last Active"
                  value={new Date(user?.lastSignInAt || "").toLocaleString()}
                />
                <DataRow
                  label="Auth Strategy"
                  value={user?.primaryEmailAddress?.verification.strategy}
                />
              </div>
            </DataCard>

            {/* Additional User Details */}
            <DataCard title="Profile Details">
              <div className="space-y-2">
                <DataRow label="Username" value={user?.username} />
                <DataRow label="First Name" value={user?.firstName} />
                <DataRow label="Last Name" value={user?.lastName} />
                <DataRow label="Profile Image" value={user?.imageUrl ? "Available" : "Not Set"} />
              </div>
            </DataCard>
          </div>

          {/* Subscription Data Grid */}
          <div className="mt-8">
            <DataCard title="Subscription Information">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Field
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500"
                      >
                        Value
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        Status
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        <StatusBadge status={subscription?.status} />
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        Plan Amount
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatCurrencyFromCents(subscription?.amount, subscription?.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        Billing Interval
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {subscription?.interval || "—"}
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        Current Period
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatDate(subscription?.currentPeriodStart)} -{" "}
                        {formatDate(subscription?.currentPeriodEnd)}
                      </td>
                    </tr>
                    <tr>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                        Started At
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                        {formatDate(subscription?.startedAt)}
                      </td>
                    </tr>
                    {subscription?.canceledAt && (
                      <>
                        <tr>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            Canceled At
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {formatDate(subscription?.canceledAt)}
                          </td>
                        </tr>
                        <tr>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            Cancellation Reason
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {subscription?.customerCancellationReason || "—"}
                          </td>
                        </tr>
                        <tr>
                          <td className="whitespace-nowrap px-6 py-4 text-sm font-medium text-gray-900">
                            Cancellation Comment
                          </td>
                          <td className="whitespace-nowrap px-6 py-4 text-sm text-gray-500">
                            {subscription?.customerCancellationComment || "—"}
                          </td>
                        </tr>
                      </>
                    )}
                  </tbody>
                </table>
              </div>
            </DataCard>
          </div>

          {/* JSON Data Preview */}
          <div className="mt-8">
            <DataCard title="Raw Data Preview">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">Clerk User Data</h3>
                  <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-4 text-sm">
                    {JSON.stringify(user, null, 2)}
                  </pre>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">Database User Data</h3>
                  <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-4 text-sm">
                    {JSON.stringify(userData, null, 2)}
                  </pre>
                </div>
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-900">Subscription Data</h3>
                  <pre className="max-h-64 overflow-auto rounded-lg bg-gray-50 p-4 text-sm">
                    {JSON.stringify(subscription, null, 2)}
                  </pre>
                </div>
              </div>
            </DataCard>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function DataCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl bg-white p-6 shadow-sm">
      <h2 className="mb-4 text-lg font-semibold text-gray-900">{title}</h2>
      {children}
    </div>
  );
}

function DataRow({ label, value }: { label: string; value: string | number | null | undefined }) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-2 last:border-0">
      <span className="text-gray-600">{label}</span>
      <span className="font-medium text-gray-900">{value || "—"}</span>
    </div>
  );
}

function formatDate(timestamp: number | undefined) {
  if (!timestamp) return "—";
  return new Date(timestamp).toLocaleDateString();
}

// Using centralized formatCurrencyFromCents from utils

function StatusBadge({ status }: { status: string | undefined }) {
  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "canceled":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <span className={`rounded-full px-2 py-1 text-sm ${getStatusColor(status)}`}>
      {status || "No status"}
    </span>
  );
}
